import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, InputGroup, NumericInput, Spinner, Switch } from '@blueprintjs/core';
import { toast } from 'sonner';
import {
  REDISTRIBUTE_CONFIRM_OVER, fetchBatches, fetchOpeners, fetchRedistributionRuns, fetchReps, newIdempotencyKey,
  previewRedistribution, retractRedistribution, saveRedistribution,
  type RedistributionRequest, type RedistributionRun, type RedistributionSource,
} from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { Card, Empty, ErrorNote, Pill, Segmented, fmtDay, fmtInt, fmtWhen, useConfirm } from './ui';
import { PickList } from '../ui/PickList';

/**
 * Redistribute (spec v4 A.12): give leads some rep already holds, or one
 * import's leads, to more reps. It only adds: the first rep keeps every lead,
 * and each run can be undone as a whole. DND leads and merchants with an open
 * deal are left out, and a rep gets a phone back only 30 days after they last
 * had it (or never, with the per-run switch). Leads are dealt out newest
 * delivery first.
 */
const DEFAULT_TOTAL = 300;

interface Receiver { rep: number; name: string; on: boolean; count: number }

/** Splits total as evenly as whole leads allow, earlier reps taking the remainder. */
function evenCounts(total: number, n: number): number[] {
  if (n === 0) return [];
  const each = Math.floor(total / n);
  return Array.from({ length: n }, (_, i) => each + (i < total % n ? 1 : 0));
}

export default function Redistribute({ initialRep, initialImport }: { initialRep?: number | null; initialImport?: number | null }) {
  const queryClient = useQueryClient();
  const reps = useQuery({ queryKey: leadImportKeys.reps, queryFn: fetchReps, ...queryPolicies.configuration });
  const openers = useQuery({ queryKey: leadImportKeys.openers, queryFn: fetchOpeners, ...queryPolicies.configuration });
  const batches = useQuery({ queryKey: leadImportKeys.batches({ status: 'Committed' }), queryFn: () => fetchBatches({ status: 'Committed' }), ...queryPolicies.pagedData });

  // From
  const [kind, setKind] = useState<'rep' | 'import'>(initialRep || !initialImport ? 'rep' : 'import');
  const [sourceRep, setSourceRep] = useState(initialRep ? String(initialRep) : '');
  const [onlyImport, setOnlyImport] = useState(initialRep && initialImport ? String(initialImport) : '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sourceImport, setSourceImport] = useState(!initialRep && initialImport ? String(initialImport) : '');

  // To
  const [rows, setRows] = useState<Receiver[]>([]);
  const [total, setTotal] = useState(DEFAULT_TOTAL);
  const [even, setEven] = useState(true);
  const [neverRepeat, setNeverRepeat] = useState(false);
  const [reason, setReason] = useState('');
  const [confirm, confirmDialog] = useConfirm();

  const source = useMemo<RedistributionSource | null>(() => {
    if (kind === 'rep') {
      if (!sourceRep) return null;
      return { kind: 'rep', rep: Number(sourceRep), imports: onlyImport ? [Number(onlyImport)] : [], date_from: dateFrom || null, date_to: dateTo || null };
    }
    return sourceImport ? { kind: 'import', import: Number(sourceImport) } : null;
  }, [kind, sourceRep, onlyImport, dateFrom, dateTo, sourceImport]);
  const fromRep = source?.kind === 'rep' ? source.rep : null;

  // Every opener starts checked; the rep the leads come from never receives them.
  useEffect(() => {
    if (!openers.data || rows.length > 0) return;
    setRows(openers.data.filter((o) => o.opener).map((o) => ({ rep: o.rep, name: o.name, on: true, count: 0 })));
  }, [openers.data, rows.length]);
  const receivers = rows.filter((r) => r.rep !== fromRep);
  const onRows = receivers.filter((r) => r.on);
  const onKey = onRows.map((r) => r.rep).join(',');
  useEffect(() => {
    if (!even || !onKey) return;
    const counts = evenCounts(total, onKey ? onKey.split(',').length : 0);
    const at = new Map(onKey.split(',').map((rep, i) => [Number(rep), counts[i]]));
    setRows((cur) => cur.map((r) => (at.has(r.rep) ? { ...r, count: at.get(r.rep) ?? 0 } : r)));
  }, [even, total, onKey]);

  const sourceCounts = useQuery({
    queryKey: leadImportKeys.redistributionPreview({ source, receivers: [] }),
    queryFn: () => previewRedistribution({ source: source as RedistributionSource, receivers: [] }),
    enabled: source != null,
    ...queryPolicies.pagedData,
  });
  const request = useMemo<RedistributionRequest | null>(() => {
    const given = onRows.filter((r) => r.count > 0).map((r) => ({ rep: r.rep, count: r.count }));
    return source && given.length ? { source, receivers: given, never_repeat: neverRepeat } : null;
  }, [source, onRows, neverRepeat]);
  const preview = useQuery({
    queryKey: leadImportKeys.redistributionPreview(request),
    queryFn: () => previewRedistribution(request as RedistributionRequest),
    enabled: request != null,
    ...queryPolicies.pagedData,
  });
  // One key per plan: a retried click replays the same run instead of adding twice.
  const requestKey = JSON.stringify(request);
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (confirmLarge: boolean) => saveRedistribution(request as RedistributionRequest, idempotencyKey, reason.trim(), confirmLarge),
    onSuccess: (r) => {
      toast.success(r.replayed ? 'This run was already saved.' : `Gave out ${fmtInt(r.summary.given)} leads.`);
      setReason('');
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const onConfirm = async () => {
    const requested = request?.receivers.reduce((t, r) => t + r.count, 0) ?? 0;
    const large = requested > REDISTRIBUTE_CONFIRM_OVER;
    if (large && !(await confirm({ title: 'Are you sure?', body: `This run gives out ${fmtInt(requested)} leads, more than ${fmtInt(REDISTRIBUTE_CONFIRM_OVER)}.`, action: `Give out ${fmtInt(requested)} leads` }))) return;
    save.mutate(large);
  };

  const setRow = (rep: number, patch: Partial<Receiver>) => setRows((cur) => cur.map((r) => (r.rep === rep ? { ...r, ...patch } : r)));
  const repOptions = (reps.data ?? []).filter((r) => r.is_active).map((r) => ({ value: String(r.id), label: r.name }));
  const importOptions = (batches.data ?? []).map((b) => ({ value: String(b.id), label: `${b.file_name}${b.batch_date ? ` (${fmtDay(b.batch_date)})` : ''}` }));
  const addable = (reps.data ?? []).filter((r) => r.is_active && r.id !== fromRep && !rows.some((x) => x.rep === r.id));
  const planned = onRows.reduce((t, r) => t + r.count, 0);
  const s = sourceCounts.data;
  const p = preview.data;

  return (
    <div className="li-stack">
      <div className="li-redis" aria-label="Redistribute">
        <section className="li-assign li-redis__step" aria-label="From">
          <div className="li-assign__head"><div className="li-assign__title"><h3>From</h3></div></div>
          <Segmented<'rep' | 'import'> ariaLabel="Redistribute from" size="sm" value={kind} onChange={setKind}
            options={[{ value: 'rep', label: "A rep's leads", hint: 'Leads one rep holds' }, { value: 'import', label: "An import's leads", hint: 'Leads of one import, whoever holds them' }]} />
          {kind === 'rep' ? (
            <>
              <label className="li-field"><span className="li-field__label">Rep</span>
                <PickList value={sourceRep} onChange={setSourceRep} fill ariaLabel="Source rep" placeholder="Choose a rep" options={repOptions} /></label>
              <label className="li-field"><span className="li-field__label">Import</span>
                <PickList value={onlyImport} onChange={setOnlyImport} fill ariaLabel="Only from import" filterable
                  options={[{ value: '', label: 'All imports' }, ...importOptions]} /></label>
              <div className="li-row">
                <label className="li-field"><span className="li-field__label">Delivered from</span>
                  <InputGroup type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="Delivered from" /></label>
                <label className="li-field"><span className="li-field__label">to</span>
                  <InputGroup type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="Delivered to" /></label>
              </div>
            </>
          ) : (
            <label className="li-field"><span className="li-field__label">Import</span>
              <PickList value={sourceImport} onChange={setSourceImport} fill ariaLabel="Source import" placeholder="Choose an import" filterable options={importOptions} /></label>
          )}
          <div aria-live="polite" data-testid="li-redis-source">
            {!source ? <span className="li-muted">{kind === 'rep' ? 'Choose the rep whose leads to share.' : 'Choose the import whose leads to share.'}</span>
              : sourceCounts.isFetching && !s ? <Spinner size={16} />
              : s && (
                <dl className="li-dl li-dl--tight">
                  <dt>Leads held</dt><dd>{fmtInt(s.source_total)}</dd>
                  <dt>Can be shared</dt><dd><b>{fmtInt(s.eligible)}</b></dd>
                  {s.excluded.dnd > 0 && <><dt>Do not call</dt><dd>{fmtInt(s.excluded.dnd)}</dd></>}
                  {s.excluded.open_deal > 0 && <><dt>Merchant has an open deal</dt><dd>{fmtInt(s.excluded.open_deal)}</dd></>}
                  {s.excluded.repeat_phone > 0 && <><dt>Same phone listed twice</dt><dd>{fmtInt(s.excluded.repeat_phone)}</dd></>}
                </dl>
              )}
          </div>
        </section>

        <section className="li-assign li-redis__step" aria-label="To">
          <div className="li-recipients__head">
            <h3>To</h3>
            <Switch checked={even} onChange={(e) => setEven((e.target as HTMLInputElement).checked)} label="Even split" alignIndicator="right" className="li-switch-sm" />
          </div>
          <label className="li-field"><span className="li-field__label">Leads in all</span>
            <NumericInput min={0} value={even ? total : planned} disabled={!even} buttonPosition="none" aria-label="Leads in all"
              onValueChange={(v) => setTotal(Math.max(0, Math.floor(v || 0)))} /></label>
          {openers.isLoading && <Spinner size={16} />}
          {openers.isSuccess && receivers.length === 0 && <span className="li-muted">No openers yet. Add a rep.</span>}
          <ul className="li-plan" aria-label="Reps receiving leads">
            {receivers.map((r) => (
              <li key={r.rep} className={`li-plan__row${r.on ? '' : ' is-off'}`}>
                <Checkbox checked={r.on} onChange={() => setRow(r.rep, { on: !r.on })} aria-label={`Include ${r.name}`} className="li-plan__check" />
                <span className="li-plan__name">{r.name}</span>
                <NumericInput min={0} value={r.count} disabled={!r.on} buttonPosition="none" className="li-plan__count" aria-label={`Leads for ${r.name}`}
                  onValueChange={(v) => { setEven(false); setRow(r.rep, { count: Math.max(0, Math.floor(v || 0)) }); }} />
              </li>
            ))}
          </ul>
          <PickList value="" placeholder="Add rep…" ariaLabel="Add rep" minimal disabled={!addable.length}
            options={addable.map((a) => ({ value: String(a.id), label: a.name }))}
            onChange={(v) => { const rep = (reps.data ?? []).find((x) => String(x.id) === v); if (rep) setRows((cur) => [...cur, { rep: rep.id, name: rep.name, on: true, count: 0 }]); }} />
          <Switch checked={neverRepeat} onChange={(e) => setNeverRepeat((e.target as HTMLInputElement).checked)} className="li-switch-sm"
            label="Never give a lead to a rep who already had it" />
          {!neverRepeat && <span className="li-muted">A rep can get a phone back 30 days after they last had it.</span>}
        </section>

        <section className="li-assign li-redis__step" aria-label="Preview">
          <div className="li-assign__head"><div className="li-assign__title"><h3>Preview</h3></div></div>
          {!request ? <span className="li-muted">Choose where the leads come from and give at least one rep some leads.</span>
            : preview.isFetching && !p ? <Spinner size={16} />
            : p && (
              <div className="li-stack" aria-live="polite" data-testid="li-redis-preview">
                <div className="li-assign__big"><b>{fmtInt(p.given)}</b> leads to <b>{p.per_rep.length === 1 ? p.per_rep[0].name : `${p.per_rep.length} reps`}</b></div>
                <table className="li-table li-table--compact li-shares">
                  <thead><tr><th>Rep</th><th className="is-num">Leads</th></tr></thead>
                  <tbody>
                    {p.per_rep.map((r) => (
                      <tr key={r.rep}>
                        <td>{r.name}{r.returning > 0 && <span className="li-subtag" title="Phones this rep had more than 30 days ago">{fmtInt(r.returning)} back</span>}</td>
                        <td className="is-num">{fmtInt(r.given)}{r.given < r.requested && <span className="li-muted"> of {fmtInt(r.requested)}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <dl className="li-dl li-dl--tight">
                  <dt>Can be shared</dt><dd>{fmtInt(p.eligible)}</dd>
                  {p.excluded.dnd > 0 && <><dt>Do not call</dt><dd>{fmtInt(p.excluded.dnd)}</dd></>}
                  {p.excluded.open_deal > 0 && <><dt>Open deal</dt><dd>{fmtInt(p.excluded.open_deal)}</dd></>}
                  {p.excluded.repeat_phone > 0 && <><dt>Same phone twice</dt><dd>{fmtInt(p.excluded.repeat_phone)}</dd></>}
                  {p.returning > 0 && <><dt>Back to a rep who had it</dt><dd>{fmtInt(p.returning)}</dd></>}
                </dl>
                {p.given < p.requested && <Pill tone="warn">Not enough leads for every count</Pill>}
                <InputGroup value={reason} placeholder="Reason" aria-label="Reason" onChange={(e) => setReason(e.target.value)} />
                <Button intent="success" fill disabled={p.given === 0 || !reason.trim()} loading={save.isPending} onClick={onConfirm}>
                  Give out {fmtInt(p.given)} leads
                </Button>
              </div>
            )}
          <ErrorNote error={preview.error ?? sourceCounts.error} />
        </section>
      </div>
      <RedistributionRuns />
      <ErrorNote error={reps.error ?? openers.error ?? batches.error} />
      {confirmDialog}
    </div>
  );
}

/** Saved runs, newest first. Undo takes every lead of the run back from the reps it went to. */
function RedistributionRuns() {
  const queryClient = useQueryClient();
  const runs = useQuery({ queryKey: leadImportKeys.redistributionRuns(null, null), queryFn: () => fetchRedistributionRuns(null, null), ...queryPolicies.pagedData });
  const [confirm, confirmDialog] = useConfirm();
  const undo = useMutation({
    mutationFn: (run: RedistributionRun) => retractRedistribution(run.id, null),
    onSuccess: (r) => {
      toast.success(r.already ? 'This run was already undone.' : `Took back ${fmtInt(r.retracted)} leads.`);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const rows = runs.data ?? [];
  return (
    <Card title="Redistribution runs" flush>
      <ErrorNote error={runs.error} />
      {runs.isLoading ? <div className="li-card__body"><Spinner size={20} /></div> : rows.length === 0 ? (
        <Empty title="No runs yet">Runs you save appear here, and each one can be undone.</Empty>
      ) : (
        <div className="li-tablewrap">
          <table className="li-table" aria-label="Redistribution runs">
            <thead><tr><th>From</th><th>To</th><th className="r">Leads</th><th>Reason</th><th>Status</th><th className="r">Saved</th><th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <b>{r.source_kind === 'rep' ? r.source_rep_name : r.source_file_name}</b>
                    {(r.date_from || r.date_to) && <span className="li-subtag">{fmtDay(r.date_from)} to {fmtDay(r.date_to)}</span>}
                  </td>
                  <td>{r.per_rep.map((x) => `${x.name} ${fmtInt(x.count)}`).join(', ') || '-'}</td>
                  <td className="r num">{fmtInt(r.given)}</td>
                  <td className="li-ellipsis">{r.reason ?? '-'}</td>
                  <td>{r.state === 'retracted'
                    ? <Pill tone="muted" title={r.retracted_by ? `by ${r.retracted_by}, ${fmtWhen(r.retracted_at)}` : undefined}>Undone</Pill>
                    : <Pill tone="success">Active</Pill>}</td>
                  <td className="r num" title={r.by ? `by ${r.by}` : undefined}>{fmtWhen(r.created_at)}</td>
                  <td className="r">
                    {r.state === 'applied' && (
                      <Button small minimal intent="danger" loading={undo.isPending && undo.variables?.id === r.id}
                        onClick={async () => {
                          if (await confirm({ title: 'Undo this run?', body: `The ${fmtInt(r.given)} leads it gave out are taken back. The reps who held them before keep them.`, action: 'Undo run', danger: true })) undo.mutate(r);
                        }}>Undo</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirmDialog}
    </Card>
  );
}
